// Input is not lost without asking (UX-011, the 2026-11-01 review).
//
// A form here is inline, beside the rail, the side bar and the scope switcher,
// and one click on any of them used to discard a half-filled site or network
// form without a word. A form with an edit nobody has sent registers here; the
// one provider holds the router's one blocker (a router honours a single
// blocker, the last registered, so one per form would guard only the newest)
// and asks before a navigation that would unmount it. Closing or reloading the
// tab is asked by the browser's own `beforeunload` prompt, whose words the
// browser chooses.
//
// The provider needs a data router (`createBrowserRouter`, `main.tsx`). Under a
// plain `<Router>` — the tests' `MemoryRouter` — there is no provider and the
// hook guards the tab's unload alone.
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Dialog } from "./surfaces";
import { useBlocker, type BlockerFunction } from "react-router-dom";

interface Registry {
  set(id: string, subject: string | undefined): void;
  delete(id: string): void;
}

const GuardContext = createContext<Registry | null>(null);

/** Guards the page while `dirty`: a navigation away asks first, and so does
 * closing the tab. `subject` names what is being edited — *Discard the changes
 * to Vieux-Port?* — and is left out where the form has no name to give.
 * `release()` stands the guard down at once, before the next render: a submit
 * calls it, because a page may navigate as soon as the write is answered. */
export function useUnsavedGuard(dirty: boolean, subject?: string): { release(): void } {
  const registry = useContext(GuardContext);
  const id = useId();
  useEffect(() => {
    if (!dirty) return;
    registry?.set(id, subject);
    const ask = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", ask);
    return () => { registry?.delete(id); window.removeEventListener("beforeunload", ask); };
  }, [dirty, subject, registry, id]);
  const release = useCallback(() => registry?.delete(id), [registry, id]);
  return { release };
}

/** The one blocker, and the question it asks. Rendered once, inside the data
 * router, around the application. */
export function UnsavedGuardProvider({ children }: { children: ReactNode }) {
  const dirty = useRef(new Map<string, string | undefined>());
  const registry = useMemo<Registry>(() => ({
    set: (id, subject) => { dirty.current.set(id, subject); },
    delete: (id) => { dirty.current.delete(id); },
  }), []);
  // A query-only change is a navigation too: the scope switcher writes the
  // address's query, and the page it leads to is another scope's.
  // The name the question gives is taken when the navigation is held, not
  // while rendering: the registry is a ref, written by effects.
  const [subject, setSubject] = useState<string>();
  const shouldBlock = useCallback<BlockerFunction>(({ currentLocation: a, nextLocation: b }) => {
    const block = dirty.current.size > 0 && (a.pathname !== b.pathname || a.search !== b.search);
    if (block) setSubject([...dirty.current.values()].find((s) => s));
    return block;
  }, []);
  const blocker = useBlocker(shouldBlock);
  return (
    <GuardContext.Provider value={registry}>
      {children}
      {blocker.state === "blocked" && (
        <DiscardQuestion subject={subject} onKeep={() => blocker.reset()}
          onDiscard={() => { dirty.current.clear(); blocker.proceed(); }} />
      )}
    </GuardContext.Provider>
  );
}

/** The question: *Keep editing* is the default, focused, and what Escape
 * answers. It sits on the shared dialog (UX-002), which contains focus, makes
 * the page behind it inert and gives focus back where it was. */
export function DiscardQuestion({ subject, onKeep, onDiscard }: { subject?: string; onKeep(): void; onDiscard(): void }) {
  const keep = useRef<HTMLButtonElement>(null);
  const question = subject ? `Discard the changes to ${subject}?` : "Discard your changes?";
  return (
    <Dialog label={question} role="alertdialog" className="guard-dialog" onClose={onKeep} initialFocus={keep}>
      <p className="guard-title">{question}</p>
      <p className="small muted">What you typed has not been saved, and leaving this page loses it.</p>
      <div className="actions">
        <button ref={keep} type="button" className="btn btn-primary" onClick={onKeep}>Keep editing</button>
        <button type="button" className="btn btn-danger" onClick={onDiscard}>Discard</button>
      </div>
    </Dialog>
  );
}
